import type { FC, FormEvent, ReactNode } from 'react'
import type { AssistantAnswer } from '@/lib/assistant'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { ArrowUp, Sparkles, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { answerQuestion, assistantSuggestions } from '@/lib/assistant'
import { LogoMark } from '@/components/generals/logo'
import { useContact } from '@/components/generals/contact-dialog'

interface ChatMessage {
    id: number
    role: 'user' | 'assistant'
    text?: string
    answer?: AssistantAnswer
}

interface ChatContextValue {
    isOpen: boolean
    isTyping: boolean
    messages: Array<ChatMessage>
    open: () => void
    close: () => void
    toggle: () => void
    ask: (question: string) => void
}

const ChatContext = createContext<ChatContextValue>({
    isOpen: false,
    isTyping: false,
    messages: [],
    open: () => undefined,
    close: () => undefined,
    toggle: () => undefined,
    ask: () => undefined
})

export const useChat = (): ChatContextValue => useContext(ChatContext)

export const ChatProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const [isOpen, setIsOpen] = useState(false)
    const [isTyping, setIsTyping] = useState(false)
    const [messages, setMessages] = useState<Array<ChatMessage>>([])
    const nextId = useRef(1)
    const timer = useRef<number | null>(null)

    useEffect(() => () => {
        if (timer.current) window.clearTimeout(timer.current)
    }, [])

    const ask = useCallback((question: string) => {
        const text = question.trim()

        if (!text) return

        setIsOpen(true)
        setMessages(previous => [...previous, { id: nextId.current++, role: 'user', text }])
        setIsTyping(true)

        if (timer.current) window.clearTimeout(timer.current)

        timer.current = window.setTimeout(() => {
            setMessages(previous => [...previous, { id: nextId.current++, role: 'assistant', answer: answerQuestion(text) }])
            setIsTyping(false)
        }, 750 + Math.min(text.length * 8, 650))
    }, [])

    const value = useMemo<ChatContextValue>(() => ({
        isOpen,
        isTyping,
        messages,
        open: () => setIsOpen(true),
        close: () => setIsOpen(false),
        toggle: () => setIsOpen(previous => !previous),
        ask
    }), [isOpen, isTyping, messages, ask])

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    )
}

const TypingDots: FC = (): ReactNode => (
    <span className='inline-flex items-center gap-1' aria-label='FlowCraft is typing'>
        {[0, 1, 2].map(index => (
            <span
                key={index}
                className='size-1.5 rounded-full bg-[#9a9aa4] animate-typing-dot'
                style={{ animationDelay: `${index * 160}ms` }}
            />
        ))}
    </span>
)

const AnswerBubble: FC<{ answer: AssistantAnswer, onCta: (message: string) => void }> = ({ answer, onCta }): ReactNode => (
    <div className='flex max-w-[92%] flex-col gap-3 rounded-[18px] rounded-tl-[6px] bg-mist px-4 py-3 text-[14px] leading-relaxed text-ink'>
        {answer.paragraphs.map(paragraph => (
            <p key={paragraph}>
                {paragraph}
            </p>
        ))}

        {answer.steps && (
            <ol className='flex flex-col gap-2'>
                {answer.steps.map((step, index) => (
                    <li key={step} className='flex gap-2.5'>
                        <span className='mt-[2px] inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-semibold text-violet ring-1 ring-hairline'>
                            {index + 1}
                        </span>

                        <span>
                            {step}
                        </span>
                    </li>
                ))}
            </ol>
        )}

        {answer.cta && (
            <button
                type='button'
                onClick={() => onCta(answer.cta?.message ?? '')}
                className='inline-flex w-fit items-center gap-1.5 rounded-full bg-ink px-3.5 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-[#26262b] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet'
            >
                {answer.cta.label}
            </button>
        )}
    </div>
)

// Floating "Ask FlowCraft" pill and the compact chat panel it opens.
export const ChatLauncher: FC = (): ReactNode => {
    const { isOpen, isTyping, messages, toggle, close, ask } = useChat()
    const { openContact } = useContact()
    const [draft, setDraft] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!isOpen) return

        const frame = window.requestAnimationFrame(() => inputRef.current?.focus())
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close()
        }

        document.addEventListener('keydown', handleKey)

        return () => {
            window.cancelAnimationFrame(frame)
            document.removeEventListener('keydown', handleKey)
        }
    }, [isOpen, close])

    useEffect(() => {
        const scroller = scrollRef.current

        if (scroller) scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' })
    }, [messages, isTyping, isOpen])

    const submit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!draft.trim()) return

        ask(draft)
        setDraft('')
    }

    const handleCta = (message: string) => {
        openContact({ intent: 'contact', message })
    }

    return (
        <>
            {isOpen && (
                <div
                    id='ask-flowcraft-panel'
                    role='dialog'
                    aria-label='Ask FlowCraft'
                    className='fc-pop-in fixed inset-x-3 bottom-[84px] z-[70] flex h-[min(600px,calc(100svh-108px))] flex-col overflow-hidden rounded-[24px] bg-white shadow-[0_28px_70px_-24px_rgba(14,14,14,0.38),0_0_0_1px_rgba(14,14,14,0.07)] sm:inset-x-auto sm:right-6 sm:w-[392px]'
                >
                    <div className='flex items-center justify-between gap-3 border-b border-hairline px-5 py-4'>
                        <div className='flex items-center gap-3'>
                            <span className='inline-flex size-9 items-center justify-center rounded-xl bg-violet-soft text-ink'>
                                <LogoMark className='h-[14px] w-5' />
                            </span>

                            <div className='flex flex-col'>
                                <span className='text-[15px] font-semibold tracking-[-0.02em]'>
                                    Ask FlowCraft
                                </span>

                                <span className='text-[12.5px] text-slate'>
                                    Workflow assistant
                                </span>
                            </div>
                        </div>

                        <button
                            type='button'
                            onClick={close}
                            aria-label='Close chat'
                            className='inline-flex size-9 items-center justify-center rounded-full text-slate transition-colors hover:bg-mist hover:text-ink focus-visible:outline-2 focus-visible:outline-violet'
                        >
                            <X className='size-[18px]' aria-hidden='true' />
                        </button>
                    </div>

                    <div ref={scrollRef} className='flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-5' aria-live='polite'>
                        <div className='max-w-[92%] rounded-[18px] rounded-tl-[6px] bg-mist px-4 py-3 text-[14px] leading-relaxed text-ink'>
                            Hi there. I can sketch how FlowCraft would automate a workflow or answer questions about how we work. Try one of these:
                        </div>

                        <div className='flex flex-col items-start gap-2 pl-1'>
                            {assistantSuggestions.map(suggestion => (
                                <button
                                    key={suggestion}
                                    type='button'
                                    onClick={() => ask(suggestion)}
                                    className='rounded-full bg-white px-3.5 py-1.5 text-left text-[13px] font-medium text-ink ring-1 ring-inset ring-[#e3e3e9] transition-colors hover:bg-violet-soft hover:ring-[#d9cdfd] focus-visible:outline-2 focus-visible:outline-violet'
                                >
                                    {suggestion}
                                </button>
                            ))}
                        </div>

                        {messages.map(message => (
                            <div key={message.id} className={cn('fc-fade-up flex', message.role === 'user' ? 'justify-end' : 'justify-start')}>
                                {message.role === 'user' ? (
                                    <div className='max-w-[85%] rounded-[18px] rounded-tr-[6px] bg-violet px-4 py-2.5 text-[14px] leading-relaxed text-white'>
                                        {message.text}
                                    </div>
                                ) : (
                                    message.answer && <AnswerBubble answer={message.answer} onCta={handleCta} />
                                )}
                            </div>
                        ))}

                        {isTyping && (
                            <div className='fc-fade-up flex'>
                                <div className='rounded-[18px] rounded-tl-[6px] bg-mist px-4 py-3.5'>
                                    <TypingDots />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='border-t border-hairline px-3 pb-3 pt-3'>
                        <form onSubmit={submit} className='flex items-center gap-2 rounded-full bg-mist py-1.5 pl-4 pr-1.5 ring-1 ring-inset ring-transparent transition-shadow focus-within:bg-white focus-within:ring-violet'>
                            <label htmlFor='ask-flowcraft-input' className='sr-only'>
                                Ask FlowCraft a question
                            </label>

                            <input
                                ref={inputRef}
                                id='ask-flowcraft-input'
                                value={draft}
                                onChange={event => setDraft(event.target.value)}
                                placeholder='Ask about your workflow...'
                                autoComplete='off'
                                className='min-w-0 flex-1 bg-transparent text-[14px] text-ink placeholder:text-[#9a9aa4] focus:outline-none'
                            />

                            <button
                                type='submit'
                                aria-label='Send question'
                                disabled={!draft.trim()}
                                className='inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-violet text-white transition-colors hover:bg-violet-bright disabled:bg-[#d6d6de] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet'
                            >
                                <ArrowUp className='size-4' aria-hidden='true' />
                            </button>
                        </form>

                        <p className='mt-2 px-2 text-center text-[12px] text-silver'>
                            Prefer a person?{' '}
                            <button
                                type='button'
                                onClick={() => openContact({ intent: 'human' })}
                                className='font-medium text-violet underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-violet'
                            >
                                Talk to our team
                            </button>
                        </p>
                    </div>
                </div>
            )}

            <button
                type='button'
                onClick={toggle}
                aria-expanded={isOpen}
                aria-controls='ask-flowcraft-panel'
                className='group fixed bottom-5 right-4 z-[70] inline-flex h-12 items-center gap-2 rounded-full bg-ink pl-4 pr-5 text-[14.5px] font-medium text-white shadow-[0_14px_34px_-12px_rgba(14,14,14,0.55),0_0_0_1px_rgba(255,255,255,0.06)_inset] transition-transform duration-300 hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-violet sm:bottom-6 sm:right-6'
            >
                {isOpen ? (
                    <X className='size-[18px]' aria-hidden='true' />
                ) : (
                    <Sparkles className='size-[18px] text-[#c9b8ff]' aria-hidden='true' />
                )}

                <span>
                    {isOpen ? 'Close' : 'Ask FlowCraft'}
                </span>
            </button>
        </>
    )
}
